import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Send, Loader2, AlertTriangle, CheckCircle2, XCircle, Info, Settings, Clock, RotateCw } from 'lucide-react';

const StatusBadge = ({ status }) => {
  const map = {
    sent: { cls: 'bg-green-100 text-green-800', icon: <CheckCircle2 className="h-4 w-4" />, label: 'ΣΤΑΛΘΗΚΕ' },
    queued: { cls: 'bg-teal-100 text-teal-800', icon: <Clock className="h-4 w-4" />, label: 'ΣΤΗΝ ΟΥΡΑ' },
    exists: { cls: 'bg-blue-100 text-blue-800', icon: <Info className="h-4 w-4" />, label: 'ΥΠΑΡΧΕΙ ΗΔΗ' },
    skipped: { cls: 'bg-gray-100 text-gray-700', icon: <AlertTriangle className="h-4 w-4" />, label: 'ΠΑΡΑΛΕΙΦΘΗΚΕ' },
    error: { cls: 'bg-red-100 text-red-800', icon: <XCircle className="h-4 w-4" />, label: 'ΣΦΑΛΜΑ' },
  };
  const s = map[status] || map.skipped;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${s.cls}`}>{s.icon}{s.label}</span>;
};

const SettingsPanel = ({ settings, onSave, folderName, onPickFolder, onTestWrite }) => {
  const [form, setForm] = useState({ method: 'browser_folder', inbox_dir: '', results_dir: '', base_url: '', company: 3, doc_type: '' });
  useEffect(() => {
    if (settings) {
      setForm({
        method: settings.method || 'browser_folder',
        inbox_dir: settings.inbox_dir || '',
        results_dir: settings.results_dir || '',
        base_url: settings.base_url || '',
        company: settings.company || 3,
        doc_type: settings.doc_type || '',
      });
    }
  }, [settings]);

  return (
    <div className="p-3 rounded bg-slate-50 border border-slate-200 space-y-3" data-testid="megasoft-settings">
      <p className="font-semibold text-sm flex items-center gap-1"><Settings className="h-4 w-4" /> Ρυθμίσεις Σύνδεσης Megasoft</p>
      <div>
        <label className="text-xs font-medium text-gray-600">Μέθοδος αποστολής</label>
        <select
          value={form.method}
          onChange={(e) => setForm({ ...form, method: e.target.value })}
          className="w-full h-9 px-2 border border-gray-300 rounded-md text-sm"
          data-testid="megasoft-method"
        >
          <option value="download">Λήψη αρχείων (download) — δουλεύει παντού (προτείνεται αν δεν γράφει στον φάκελο)</option>
          <option value="browser_folder">Απευθείας στον φάκελο μέσω browser (Chrome/Edge)</option>
          <option value="agent_file">Αρχείο στον server (μόνο αν το πρόγραμμα τρέχει τοπικά)</option>
          <option value="http">Απευθείας σύνδεση (HTTP API)</option>
        </select>
      </div>

      {form.method === 'download' ? (
        <div className="p-2 rounded bg-white border text-xs text-gray-700 space-y-1">
          <p>Ο browser θα <b>κατεβάζει</b> τα αρχεία <code>.json</code>. Για να πηγαίνουν κατευθείαν στον agent:</p>
          <p>1. Chrome/Edge → <b>Ρυθμίσεις → Λήψεις (Downloads)</b>.</p>
          <p>2. Άλλαξε τον φάκελο λήψεων σε <b>C:\\MegasoftAgent\\inbox</b> (ή τον δικό σου).</p>
          <p>3. Απενεργοποίησε το «Ερώτηση κάθε φορά» ώστε να μην ρωτάει.</p>
          <p className="text-teal-700">Έτσι κάθε αποστολή κατεβαίνει απευθείας στον inbox.</p>
        </div>
      ) : form.method === 'browser_folder' ? (
        <div className="space-y-2">
          <div className="p-2 rounded bg-white border text-xs text-gray-600">
            Ο browser (Chrome/Edge) θα γράφει τα αρχεία <b>απευθείας</b> στον τοπικό φάκελο inbox του agent. Επίλεξε τον φάκελο μία φορά.
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onPickFolder} data-testid="megasoft-pick-folder">
              📁 Επιλογή φακέλου inbox
            </Button>
            <span className="text-sm">
              {folderName ? <span className="text-green-700 font-medium">✓ {folderName}</span> : <span className="text-amber-700">Δεν έχει επιλεγεί φάκελος</span>}
            </span>
          </div>
          {folderName && (
            <Button size="sm" variant="outline" onClick={onTestWrite} className="border-teal-400 text-teal-700" data-testid="megasoft-test-write">
              🧪 Δοκιμή εγγραφής (γράφει δοκιμαστικό αρχείο & δείχνει το περιεχόμενο)
            </Button>
          )}
        </div>
      ) : form.method === 'agent_file' ? (
        <>
          <div>
            <label className="text-xs font-medium text-gray-600">Φάκελος inbox του Megasoft agent (στον server)</label>
            <Input
              value={form.inbox_dir}
              onChange={(e) => setForm({ ...form, inbox_dir: e.target.value })}
              placeholder="π.χ. C:\\MegasoftAgent\\inbox"
              className="text-sm"
              data-testid="megasoft-inbox-dir"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Φάκελος αποτελεσμάτων (προαιρετικό)</label>
            <Input
              value={form.results_dir}
              onChange={(e) => setForm({ ...form, results_dir: e.target.value })}
              placeholder="π.χ. C:\\MegasoftAgent\\results"
              className="text-sm"
              data-testid="megasoft-results-dir"
            />
          </div>
        </>
      ) : (
        <div>
          <label className="text-xs font-medium text-gray-600">Διεύθυνση Megasoft API</label>
          <Input
            value={form.base_url}
            onChange={(e) => setForm({ ...form, base_url: e.target.value })}
            placeholder="π.χ. https://megasoft.myprintermanager.gr"
            className="text-sm"
            data-testid="megasoft-base-url"
          />
          {settings && !settings.has_key && (
            <p className="text-xs text-amber-700 mt-1">⚠️ Το κλειδί API δεν έχει οριστεί (ρυθμίζεται με ασφαλή τρόπο).</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-600">Εταιρεία Megasoft</label>
          <Input type="number" value={form.company} onChange={(e) => setForm({ ...form, company: Number(e.target.value) })} className="text-sm" data-testid="megasoft-company" />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600">Τύπος παραστατικού (doc_type, προαιρετικό)</label>
          <Input value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} placeholder="π.χ. 6" className="text-sm" data-testid="megasoft-doc-type" />
        </div>
      </div>

      <Button size="sm" onClick={() => onSave(form)} className="bg-slate-700 hover:bg-slate-800" data-testid="megasoft-settings-save">
        💾 Αποθήκευση Ρυθμίσεων
      </Button>
    </div>
  );
};

const MegasoftModal = ({ preview, loading, sending, results, settings, onSaveSettings, history, onResend, folderName, onPickFolder, onTestWrite, onSend, onClose }) => {
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const orders = preview?.orders || [];
  const warnings = preview?.warnings || [];
  const configured = preview?.configured;
  const method = preview?.method || settings?.method;
  const historyList = history || [];
  const sentRefs = new Set(historyList.filter(h => ['sent', 'queued', 'exists'].includes(h.status)).map(h => h.external_ref));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="megasoft-modal">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              📤 Αποστολή Χρεώσεων στο Λογιστικό (Megasoft)
            </CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowHistory(v => !v)} data-testid="megasoft-history-toggle">
                <Clock className="h-4 w-4 mr-1" /> Ιστορικό{historyList.length ? ` (${historyList.length})` : ''}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowSettings(v => !v)} data-testid="megasoft-settings-toggle">
                <Settings className="h-4 w-4 mr-1" /> Ρυθμίσεις
              </Button>
            </div>
          </div>
          {preview && (
            <p className="text-sm text-gray-600">
              Περίοδος: {preview.month_name} {preview.year}
              {method && <span className="ml-2 text-xs bg-slate-100 px-2 py-0.5 rounded">Μέθοδος: {method === 'agent_file' ? 'Αρχείο agent' : method === 'browser_folder' ? 'Τοπικός φάκελος (browser)' : method === 'download' ? 'Λήψη αρχείων' : 'HTTP API'}</span>}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {showSettings && <SettingsPanel settings={settings} onSave={onSaveSettings} folderName={folderName} onPickFolder={onPickFolder} onTestWrite={onTestWrite} />}

          {showHistory && (
            <div className="p-3 rounded bg-slate-50 border border-slate-200" data-testid="megasoft-history">
              <p className="font-semibold text-sm mb-2 flex items-center gap-1"><Clock className="h-4 w-4" /> Ιστορικό Αποστολών ({preview?.month_name} {preview?.year})</p>
              {historyList.length === 0 ? (
                <p className="text-sm text-gray-500">Καμία αποστολή για αυτόν τον μήνα ακόμη.</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {historyList.map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-xs border-b last:border-0 py-1">
                      <div>
                        <span className="font-medium">{h.customer_name}</span>
                        {h.order_number && <span className="text-gray-500"> • Αρ. {h.order_number}</span>}
                        <span className="text-gray-400"> • {String(h.sent_at || '').slice(0, 16).replace('T', ' ')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={h.status} />
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-xs px-2"
                          onClick={() => onResend && onResend(h)}
                          data-testid={`megasoft-resend-${h.external_ref}`}
                          title="Επαναποστολή (έχει ήδη σταλεί)"
                        >
                          <RotateCw className="h-3 w-3 mr-1" /> Επαναποστολή
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Υπολογισμός χρεώσεων...
            </div>
          ) : results ? (
            // ===== RESULTS VIEW =====
            <div className="space-y-3" data-testid="megasoft-results">
              <div className="p-3 rounded bg-indigo-50 border border-indigo-200 text-indigo-900 font-semibold">
                Στάλθηκαν {results.sent_count} από {results.total_orders} παραγγελίες.
              </div>
              {results.method === 'download' && (
                <div className="p-2 rounded bg-teal-50 border border-teal-200 text-xs text-teal-900">
                  📥 Τα αρχεία κατέβηκαν από τον browser. Έλεγξε τον φάκελο <b>Λήψεων (Downloads)</b> — ή ρύθμισε τον browser να κατεβάζει στον φάκελο inbox του agent.
                </div>
              )}
              {results.method === 'browser_folder' && (
                <div className="p-2 rounded bg-teal-50 border border-teal-200 text-xs text-teal-900">
                  📁 Φάκελος: <b>{results.folder || '—'}</b> • Αρχεία .json στον φάκελο τώρα: <b>{(results.present || []).length}</b>
                  {(results.present || []).length > 0 && (
                    <div className="mt-1 font-mono break-all">{(results.present || []).slice(0, 20).join(', ')}</div>
                  )}
                  {(results.present || []).length === 0 && (
                    <div className="mt-1 text-amber-700">⚠️ Ο φάκελος φαίνεται κενός από .json. Αν ο Megasoft agent «τρέχει», ίσως τα πήρε ήδη. Αλλιώς ίσως επιλέχθηκε λάθος φάκελος — δοκίμασε «🧪 Δοκιμή εγγραφής» στις Ρυθμίσεις.</div>
                  )}
                </div>
              )}
              {(results.results || []).map((r, i) => (
                <div key={i} className="flex items-center justify-between border rounded px-3 py-2 text-sm">
                  <div>
                    <p className="font-medium">{r.customer}</p>
                    {r.number && <p className="text-xs text-gray-500">Αρ. παραγγελίας: {r.number}{r.totals ? ` • Σύνολο: ${r.totals.total}€` : ''}</p>}
                    {r.message && <p className="text-xs text-gray-500">{r.message}</p>}
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              ))}
              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={onClose} data-testid="megasoft-close">ΚΛΕΙΣΙΜΟ</Button>
              </div>
            </div>
          ) : (
            // ===== PREVIEW VIEW =====
            <div className="space-y-4" data-testid="megasoft-preview">
              {!configured && (
                <div className="p-3 rounded bg-amber-50 border border-amber-300 text-amber-900 text-sm flex gap-2">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                  <span>Η σύνδεση με το Megasoft δεν έχει ρυθμιστεί ακόμη. Πατήστε «Ρυθμίσεις» πάνω δεξιά για να ορίσετε τη μέθοδο και τον φάκελο inbox (ή τη διεύθυνση). Μπορείτε να δείτε την προεπισκόπηση τώρα.</span>
                </div>
              )}

              {warnings.length > 0 && (
                <div className="p-3 rounded bg-orange-50 border border-orange-200 text-sm">
                  <p className="font-semibold text-orange-800 mb-1 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Προειδοποιήσεις ({warnings.length})</p>
                  <ul className="list-disc list-inside text-orange-700 space-y-0.5 max-h-40 overflow-y-auto">
                    {warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {orders.length === 0 ? (
                <p className="text-center py-6 text-gray-500">Δεν υπάρχουν χρεώσεις προς αποστολή για αυτόν τον μήνα.</p>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">{orders.length} παραγγελίες (μία ανά πελάτη). Κάθε γραμμή = ένας κωδικός χρέωσης.</p>
                  {orders.map((o, idx) => (
                    <div key={idx} className="border rounded-lg" data-testid={`megasoft-order-${o.customer_id}`}>
                      <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-sm">{o.customer_name}</p>
                          <p className="text-xs text-gray-500">
                            {o.megasoft_code ? `Κωδ. Megasoft: ${o.megasoft_code}` : (o.afm ? `ΑΦΜ: ${o.afm} (αναζήτηση)` : '⚠️ χωρίς κωδικό/ΑΦΜ')}
                            {' • '}ref: {o.external_ref}
                          </p>
                        </div>
                        <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded">{o.lines.length} γραμμές</span>
                        {sentRefs.has(o.external_ref) && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded inline-flex items-center gap-1"><Info className="h-3 w-3" />ήδη σταλμένο</span>
                        )}
                      </div>
                      <table className="w-full text-xs">
                        <thead className="bg-white border-b">
                          <tr className="text-left text-gray-500">
                            <th className="px-3 py-1">ΚΩΔΙΚΟΣ</th>
                            <th className="px-3 py-1">ΤΥΠΟΣ</th>
                            <th className="px-3 py-1 text-right">ΠΟΣΟΤ.</th>
                            <th className="px-3 py-1">ΜΗΧΑΝΗΜΑ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {o.lines.map((l, li) => (
                            <tr key={li} className="border-b last:border-0">
                              <td className="px-3 py-1 font-mono font-semibold">{l.item}</td>
                              <td className="px-3 py-1">{l.kind}</td>
                              <td className="px-3 py-1 text-right">{l.qty.toLocaleString()}</td>
                              <td className="px-3 py-1 text-gray-500">{l.machine_serial}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={onClose} data-testid="megasoft-cancel">ΑΚΥΡΟ</Button>
                <Button
                  onClick={onSend}
                  disabled={!configured || sending || orders.length === 0 || (method === 'browser_folder' && !folderName)}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="megasoft-send"
                  title={method === 'browser_folder' && !folderName ? 'Επίλεξε πρώτα τον φάκελο inbox (Ρυθμίσεις)' : (!configured ? 'Ρύθμισε πρώτα τη σύνδεση με το Megasoft' : '')}
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                  {sending ? 'Αποστολή...' : (method === 'browser_folder' ? 'ΕΓΓΡΑΦΗ ΣΤΟΝ ΦΑΚΕΛΟ INBOX' : method === 'download' ? 'ΛΗΨΗ ΑΡΧΕΙΩΝ (.json)' : 'ΑΠΟΣΤΟΛΗ ΣΤΟ MEGASOFT')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MegasoftModal;
